if (!customElements.get("abbello-cart-prescription")) {
  customElements.define(
    "abbello-cart-prescription",
    class AbbelloCartPrescription extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.productSelect = this.querySelector(
          ".abbello-cart-rx__product-select",
        );
        this.details = this.querySelector(".abbello-cart-rx__details");
        this.message = this.querySelector(".abbello-cart-rx__message");
        this.fileInput = this.querySelector(".abbello-cart-rx__file");
        this.hasChoice = this.querySelector('[data-choice="has"]');
        this.assistanceChoice = this.querySelector(
          '[data-choice="assistance"]',
        );
        this.manualSave = this.querySelector("[data-save-manual]");
        this.uploadButton = this.querySelector("[data-upload]");
        this.initializeSelects();
        this.bindEvents();
        this.loadCart()
          .then(() => this.renderSelectedItem())
          .catch(() => {
            this.showMessage(this.dataset.saveError, true);
          });
      }

      bindEvents() {
        this.hasChoice.addEventListener("click", () => {
          this.resetChoices();
          this.details.hidden = false;
          this.activateTab("manual");
        });
        this.assistanceChoice.addEventListener("click", () => {
          this.saveAssistanceRequest();
        });
        this.productSelect.addEventListener("change", () => {
          this.renderSelectedItem();
        });
        this.querySelectorAll("[data-tab]").forEach((button) => {
          button.addEventListener("click", () => {
            this.activateTab(button.dataset.tab);
          });
        });
        this.manualSave.addEventListener("click", () => {
          this.saveManualPrescription();
        });
        this.uploadButton.addEventListener("click", () => {
          this.fileInput.value = "";
          this.fileInput.click();
        });
        this.fileInput.addEventListener("change", () => {
          const file = this.fileInput.files?.[0];
          if (file) this.uploadPrescription(file);
        });
      }

      initializeSelects() {
        const configs = {
          sphere: { min: -14, max: 10, step: 0.25, signed: true },
          cylinder: { min: -6, max: 6, step: 0.25, signed: true },
          axis: { min: 0, max: 180, step: 1, suffix: "°" },
          addition: {
            min: 0.5,
            max: 4,
            step: 0.25,
            signed: true,
            empty: "-",
          },
          pd: { min: 40, max: 80, step: 0.5, suffix: "mm", empty: "-" },
        };

        this.querySelectorAll("select[data-values]").forEach((select) => {
          const config = configs[select.dataset.values];
          if (!config) return;
          if (config.empty) select.add(new Option(config.empty, "0"));
          const scale = 100;
          for (
            let current = Math.round(config.min * scale);
            current <= Math.round(config.max * scale);
            current += Math.round(config.step * scale)
          ) {
            const value = current / scale;
            let label = Number.isInteger(config.step)
              ? String(value)
              : value.toFixed(config.step < 1 ? 2 : 0);
            if (config.signed && value > 0) label = `+${label}`;
            select.add(new Option(`${label}${config.suffix || ""}`, value));
          }
          select.value = "0";
        });
      }

      activateTab(tabName) {
        this.querySelectorAll("[data-tab]").forEach((button) => {
          button.setAttribute(
            "aria-selected",
            String(button.dataset.tab === tabName),
          );
        });
        this.querySelectorAll("[data-panel]").forEach((panel) => {
          panel.hidden = panel.dataset.panel !== tabName;
        });
      }

      async loadCart() {
        const response = await fetch(this.dataset.cartUrl, {
          credentials: "same-origin",
        });
        if (!response.ok) throw new Error("cart");
        this.cart = await response.json();
      }

      selectedLine() {
        return Number(this.productSelect.value);
      }

      selectedItem() {
        return this.cart?.items?.[this.selectedLine() - 1] || null;
      }

      renderSelectedItem() {
        const item = this.selectedItem();
        if (!item) return;
        const properties = item.properties || {};
        const status = properties._rx_status || "";
        this.resetChoices();
        this.querySelectorAll("[data-rx-property]").forEach((field) => {
          const value = String(properties[field.dataset.rxProperty] || "0");
          field.value = Array.from(field.options).some(
            (option) => option.value === value,
          )
            ? value
            : "0";
        });

        if (status === "Assistenza ottico richiesta") {
          this.confirmChoice(
            this.assistanceChoice,
            this.dataset.assistanceSuccess,
          );
        } else if (status === "Prescrizione inserita manualmente") {
          this.confirmChoice(this.hasChoice, this.dataset.manualSuccess);
        } else if (status === "Prescrizione caricata") {
          this.confirmChoice(this.hasChoice, this.dataset.uploadSuccess);
        }
      }

      resetChoices() {
        [this.hasChoice, this.assistanceChoice].forEach((choice) => {
          choice.classList.remove("is-confirmed");
          choice.setAttribute("aria-pressed", "false");
          const copy = choice.querySelector("small");
          copy.textContent = copy.dataset.defaultText;
        });
        this.message.hidden = true;
        this.message.classList.remove("is-error");
      }

      confirmChoice(choice, message) {
        choice.classList.add("is-confirmed");
        choice.setAttribute("aria-pressed", "true");
        choice.querySelector("small").textContent = `${message} ✓`;
      }

      zeroPrescription(status) {
        return {
          _rx_status: status,
          _rx_od_sph: "0",
          _rx_od_cyl: "0",
          _rx_od_axis: "0",
          _rx_od_add: "0",
          _rx_os_sph: "0",
          _rx_os_cyl: "0",
          _rx_os_axis: "0",
          _rx_os_add: "0",
          _rx_pd: "0",
        };
      }

      manualPrescription() {
        const values = this.zeroPrescription(
          "Prescrizione inserita manualmente",
        );
        this.querySelectorAll("[data-rx-property]").forEach((field) => {
          values[field.dataset.rxProperty] = field.value || "0";
        });
        return values;
      }

      async saveAssistanceRequest() {
        await this.withLoading(this.assistanceChoice, async () => {
          await this.updateLine(
            this.zeroPrescription("Assistenza ottico richiesta"),
          );
          this.details.hidden = true;
          this.renderSelectedItem();
        });
      }

      async saveManualPrescription() {
        await this.withLoading(this.manualSave, async () => {
          await this.updateLine(this.manualPrescription());
          this.details.hidden = true;
          this.renderSelectedItem();
        });
      }

      async uploadPrescription(file) {
        await this.withLoading(this.uploadButton, async () => {
          const body = new FormData();
          body.append("file", file);
          const response = await fetch(this.dataset.uploadUrl, {
            method: "POST",
            body,
          });
          if (!response.ok) throw new Error("upload");
          const upload = await response.json();
          const values = this.zeroPrescription("Prescrizione caricata");
          values._rx_file_url = upload.file_url;
          values._rx_file_name = file.name;
          await this.updateLine(values);
          this.details.hidden = true;
          this.renderSelectedItem();
        });
      }

      async withLoading(button, action) {
        const originalText = button.textContent;
        button.disabled = true;
        if (button === this.manualSave || button === this.uploadButton) {
          button.textContent = this.dataset.loading;
        }
        this.message.hidden = true;
        try {
          await action();
        } catch (error) {
          this.showMessage(this.dataset.saveError, true);
        } finally {
          button.disabled = false;
          if (button === this.manualSave || button === this.uploadButton) {
            button.textContent = originalText;
          }
        }
      }

      async updateLine(rxProperties) {
        if (!this.cart) await this.loadCart();
        const line = this.selectedLine();
        const item = this.selectedItem();
        if (!item) throw new Error("item");
        const properties = { ...(item.properties || {}) };
        Object.keys(properties).forEach((key) => {
          if (key.startsWith("_rx_")) delete properties[key];
        });
        Object.assign(properties, rxProperties);

        const response = await fetch(this.dataset.cartChangeUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            line,
            quantity: item.quantity,
            properties,
          }),
        });
        if (!response.ok) throw new Error("change");
        const cart = await response.json();
        if (cart.errors) throw new Error("change");
        this.cart = cart;
        await this.syncCartNote().catch(() => {});
      }

      async syncCartNote() {
        const start = "--- ABBELLO PRESCRIZIONE ---";
        const end = "--- FINE ABBELLO PRESCRIZIONE ---";
        const existingNote = String(this.cart.note || "")
          .replace(new RegExp(`${start}[\\s\\S]*?${end}`, "g"), "")
          .trim();
        const lines = this.cart.items
          .filter((item) => item.properties?._rx_status)
          .flatMap((item) => {
            const properties = item.properties;
            const itemLines = [
              item.product_title,
              `Stato: ${properties._rx_status}`,
            ];
            if (properties._rx_status === "Prescrizione inserita manualmente") {
              itemLines.push(
                `OD: SPH ${properties._rx_od_sph} | CYL ${properties._rx_od_cyl} | Asse ${properties._rx_od_axis} | ADD ${properties._rx_od_add}`,
                `OS: SPH ${properties._rx_os_sph} | CYL ${properties._rx_os_cyl} | Asse ${properties._rx_os_axis} | ADD ${properties._rx_os_add}`,
                `Distanza pupillare: ${properties._rx_pd}`,
              );
            }
            itemLines.push(
              `File prescrizione: ${properties._rx_file_url || "Non caricato"}`,
            );
            return itemLines;
          });
        const note = [existingNote, [start, ...lines, end].join("\n")]
          .filter(Boolean)
          .join("\n\n");
        const response = await fetch(this.dataset.cartUpdateUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ note }),
        });
        if (!response.ok) throw new Error("note");
        this.cart = await response.json();
      }

      showMessage(message, isError) {
        this.message.textContent = message;
        this.message.classList.toggle("is-error", Boolean(isError));
        this.message.hidden = false;
      }
    },
  );
}
